/**
 * Application entry point.
 *
 * Wiring only: the page loads MNIST, starts the worker, and connects UI events
 * to worker messages. Business logic lives in the engine and UI modules.
 */
import {
    LIMITS,
    canAddLayer,
    canAddNeuron,
    defaultConfig,
    parameterCount,
} from './engine/architecture.ts';
import type { LayerSpec, NetworkConfig, StepMetrics, TrainingSettings } from './engine/types.ts';
import { ConfirmDialog } from './ui/confirm-dialog.ts';
import { DrawPad } from './ui/draw-pad.ts';
import { EDGE_PALETTES } from './ui/edge-palette.ts';
import { EpochProgress } from './ui/epoch-progress.ts';
import { MetricsChart } from './ui/metrics-chart.ts';
import { MetricsControls } from './ui/metrics-controls.ts';
import { checkAvailability, loadMnist } from './ui/mnist-loader.ts';
import { NetworkGraph } from './ui/network-graph.ts';
import { SnapshotStore } from './ui/snapshots.ts';
import { TrainingClient } from './worker/client.ts';

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing element #${id}`);
  }
  return element as T;
}

function setStatus(text: string, kind: 'info' | 'error' | 'success' = 'info'): void {
  byId<HTMLSpanElement>('status-text').textContent = text;
  byId<HTMLDivElement>('status').dataset.kind = kind;
}

/** Reads the current architecture controls; only one hidden layer is editable. */
function readConfig(): NetworkConfig {
  const units = Number(byId<HTMLInputElement>('hidden-units').value);
  const layers: LayerSpec[] = [{ id: 'h1', units }];
  return { ...defaultConfig(), hidden: layers };
}

function readSettings(): TrainingSettings {
  return {
    batchSize: Number(byId<HTMLSelectElement>('batch-size').value),
    learningRate: Number(byId<HTMLSelectElement>('learning-rate').value),
  };
}

function renderParameterSummary(config: NetworkConfig): void {
  byId<HTMLSpanElement>('param-count').textContent = String(parameterCount(config));
}

function main(): void {
  const store = new SnapshotStore();
  const chart = new MetricsChart(byId<HTMLCanvasElement>('metrics-chart'));
  const epochProgress = new EpochProgress(
    byId<HTMLSpanElement>('epoch-label'),
    byId<HTMLSpanElement>('epoch-batches'),
    byId<HTMLDivElement>('epoch-progress'),
  );
  // Controls own the DOM state; the chart owns the rendering. The callback is the
  // only bridge between them, so a click and an incoming batch render identically.
  new MetricsControls(
    byId<HTMLDivElement>('metrics-controls'),
    byId<HTMLParagraphElement>('metric-hint'),
    (selection) => chart.configure(selection),
  );

  const graph = new NetworkGraph(byId<HTMLDivElement>('network-canvas'), {
    width: 720,
    height: 440,
    legend: byId<HTMLDivElement>('edge-legend'),
    onSelect: (node) => {
      const inspector = byId<HTMLDivElement>('inspector');
      if (!node) {
        inspector.textContent = 'Select a neuron to inspect it.';
      } else if (node.layerIndex === 1) {
        // The first hidden layer reads pixels directly, so its field is the exact
        // set of weights behind the neuron.
        inspector.textContent = `Neuron ${node.id}: the input tile shows which pixels it responds to (colour = excitatory or inhibitory weight).`;
      } else {
        // Deeper layers have no direct pixel weights; the field is the linear
        // sensitivity through the intervening kernels, which ignores that ReLU
        // gates part of the path.
        inspector.textContent = `Neuron ${node.id}: the input tile shows which pixels it responds to, traced back through the earlier layers (approximate, since activations are ignored).`;
      }
    },
  });

  // The palette lives in the radio group like every other switch: the checked
  // input is the state, and the graph only reacts to it.
  const paletteControls = byId<HTMLDivElement>('graph-toolbar');
  paletteControls.addEventListener('change', () => {
    const checked = paletteControls.querySelector<HTMLInputElement>(
      'input[name="edge-palette"]:checked',
    );
    const palette = EDGE_PALETTES.find((candidate) => candidate === checked?.value);
    if (palette) {
      graph.setPalette(palette);
    }
  });

  const pad = new DrawPad(byId<HTMLCanvasElement>('draw-pad'), { size: 280 });
  const client = new TrainingClient(new URL('./training.worker.js', import.meta.url));
  const resetDialog = new ConfirmDialog(byId<HTMLDivElement>('reset-dialog'));
  let config = readConfig();
  let settings = readSettings();
  let initialized = false;
  let training = false;
  let sampleTimer: number | null = null;
  let previewTimer: number | null = null;

  /**
   * The input tile shows whichever training sample the network is being fed.
   * Sampling lives in the worker because only it holds the dataset.
   */
  const SAMPLE_INTERVAL_MS = 2000;
  /**
   * Dragging the slider fires an event per pixel; rebuilding the topology on
   * every one of them would queue dozens of throwaway networks in the worker.
   */
  const PREVIEW_DEBOUNCE_MS = 150;

  function stopSampleRotation(): void {
    if (sampleTimer !== null) {
      window.clearInterval(sampleTimer);
      sampleTimer = null;
    }
  }

  function startSampleRotation(): void {
    stopSampleRotation();
    client.send({ type: 'sample' });
    sampleTimer = window.setInterval(() => client.send({ type: 'sample' }), SAMPLE_INTERVAL_MS);
  }

  /**
   * Asks the worker for an untrained topology so the graph is never empty.
   *
   * Only valid before the first `init`: once a dataset is loaded, replacing the
   * controller would throw away learned weights.
   */
  function requestPreview(): void {
    if (training || initialized) {
      return;
    }
    client.send({ type: 'preview', payload: { config, settings } });
  }

  function schedulePreview(): void {
    if (previewTimer !== null) {
      window.clearTimeout(previewTimer);
    }
    previewTimer = window.setTimeout(() => {
      previewTimer = null;
      requestPreview();
    }, PREVIEW_DEBOUNCE_MS);
  }

  byId<HTMLSpanElement>('hidden-units-value').textContent = String(config.hidden[0]?.units ?? 0);
  renderParameterSummary(config);

  /** Single place that owns button enablement, so states cannot drift apart. */
  function updateControls(): void {
    byId<HTMLButtonElement>('play-button').disabled = training;
    byId<HTMLButtonElement>('pause-button').disabled = !training;
    byId<HTMLButtonElement>('reset-button').disabled = training || !initialized;
    // Growth rebuilds the model and optimizer, which is only safe while paused.
    byId<HTMLButtonElement>('add-neuron-button').disabled = training;
    byId<HTMLButtonElement>('add-layer-button').disabled = training;
    byId<HTMLParagraphElement>('grow-hint').hidden = !training;
    // Initial config only reaches the model through `init`, so it locks once a
    // network exists; growing it is what the "Grow the Network" card is for.
    const configLocked = training || initialized;
    byId<HTMLInputElement>('hidden-units').disabled = configLocked;
    byId<HTMLSelectElement>('batch-size').disabled = configLocked;
    byId<HTMLSelectElement>('learning-rate').disabled = configLocked;
    // Greyed-out inputs still look usable, so the locked card names the reason
    // instead of relying on the disabled styling alone.
    byId<HTMLDivElement>('config-overlay').hidden = !configLocked;
    // Predictions need a trained network that is not mid-step; anything drawn
    // before that could not be used, so the whole card is locked instead.
    const drawLocked = training || !initialized;
    byId<HTMLCanvasElement>('draw-pad').classList.toggle('is-locked', drawLocked);
    byId<HTMLButtonElement>('predict-button').disabled = drawLocked;
    byId<HTMLButtonElement>('clear-button').disabled = drawLocked;
    byId<HTMLDivElement>('draw-overlay').hidden = !drawLocked;
    byId<HTMLParagraphElement>('draw-overlay-message').textContent = training
      ? 'Pause training to draw a digit and run a prediction.'
      : 'Press play to train a network before drawing a digit.';
  }

  function setRunning(running: boolean): void {
    training = running;
    updateControls();
  }

  client.on('ready', ({ snapshot }) => {
    initialized = true;
    graph.render(snapshot);
    // A square that stays black until training starts looks broken, so show one
    // sample as soon as the dataset is available.
    client.send({ type: 'sample' });
    renderParameterSummary(config);
    // Enablement depends on `initialized`, so refresh it here; `training` may
    // already be true because `start` is sent immediately after `init`.
    updateControls();
  });

  client.on('previewed', ({ snapshot, config: previewConfig }) => {
    if (initialized) {
      return;
    }
    config = previewConfig;
    graph.render(snapshot);
    renderParameterSummary(config);
    updateControls();
  });

  client.on('step', (metrics: { metrics: StepMetrics }) => {
    const point = store.addStep(metrics.metrics);
    chart.setData(store.metrics);
    epochProgress.update(metrics.metrics);
    byId<HTMLSpanElement>('step-count').textContent = String(point.step);
    byId<HTMLSpanElement>('loss-value').textContent = point.loss.toFixed(4);
    byId<HTMLSpanElement>('accuracy-value').textContent = `${(point.accuracy * 100).toFixed(1)}%`;
  });

  client.on('epoch', ({ metrics }) => {
    store.addEpoch(metrics);
    byId<HTMLSpanElement>('test-accuracy-value').textContent =
      `${(metrics.testAccuracy * 100).toFixed(1)}%`;
  });

  client.on('grown', ({ snapshot, config: grownConfig, record }) => {
    config = grownConfig;
    store.markGrowth();
    store.addSnapshot({
      snapshot,
      step: store.metrics.at(-1)?.step ?? 0,
      reason: 'growth',
      label: record,
    });
    graph.render(snapshot);
    renderParameterSummary(config);
    chart.setData(store.metrics);
    setStatus(record, 'success');
  });

  client.on('sample', ({ pixels, label, probabilities }) => {
    graph.setInputImage(pixels, label);
    graph.setPredictions(probabilities);
  });

  client.on('prediction', ({ probabilities }) => {
    const ranked = Array.from(probabilities)
      .map((probability, digit) => ({ probability, digit }))
      .sort((a, b) => b.probability - a.probability);
    const best = ranked[0];
    byId<HTMLSpanElement>('prediction-value').textContent = best ? String(best.digit) : '-';
    byId<HTMLSpanElement>('prediction-confidence').textContent = best
      ? `${(best.probability * 100).toFixed(1)}%`
      : '-';
    setStatus('Prediction updated.', 'info');
  });

  client.onError((message) => setStatus(message, 'error'));

  async function startTraining(): Promise<void> {
    if (training) {
      return;
    }

    // Only the first start loads the dataset; later starts resume the existing
    // model so that learned weights are never discarded. A preview controller is
    // dataset-free, so `init` replaces it rather than reusing it.
    if (!initialized) {
      const availability = await checkAvailability();
      if (!availability.available) {
        setStatus('MNIST data missing on the server. Run "npm run data:fetch" first.', 'error');
        return;
      }

      setStatus('Loading MNIST...', 'info');
      const raw = await loadMnist();
      // Settings only reach the model through `init`, so refresh them here to
      // pick up dropdown changes made since the last reset.
      settings = readSettings();
      client.send({ type: 'init', payload: { config, settings, ...raw } }, [
        raw.trainImages.buffer,
        raw.trainLabels.buffer,
        raw.testImages.buffer,
        raw.testLabels.buffer,
      ]);
    }

    client.send({ type: 'start' });
    setRunning(true);
    startSampleRotation();
    setStatus('Training...', 'info');
  }

  function pauseTraining(): void {
    client.send({ type: 'pause' });
    setRunning(false);
    stopSampleRotation();
    setStatus('Paused. Change the network, or press play to resume.', 'info');
  }

  /**
   * Discards the model along with its metrics. The dataset lives in the
   * worker's controller, which the reset disposes, so the next play re-downloads
   * MNIST: a reset is meant to start from a freshly initialized network.
   */
  async function resetTraining(): Promise<void> {
    if (training || !initialized) {
      return;
    }

    if (!(await resetDialog.ask())) {
      return;
    }

    client.send({ type: 'reset' });
    initialized = false;
    stopSampleRotation();
    graph.setInputImage(null, null);
    graph.setPredictions(null);
    config = readConfig();
    store.clear();
    chart.setData(store.metrics);
    epochProgress.reset();
    byId<HTMLSpanElement>('hidden-units-value').textContent = String(config.hidden[0]?.units ?? 0);
    byId<HTMLSpanElement>('step-count').textContent = '0';
    byId<HTMLSpanElement>('loss-value').textContent = '-';
    byId<HTMLSpanElement>('accuracy-value').textContent = '-';
    byId<HTMLSpanElement>('test-accuracy-value').textContent = '-';
    byId<HTMLSpanElement>('prediction-value').textContent = '-';
    byId<HTMLSpanElement>('prediction-confidence').textContent = '-';
    renderParameterSummary(config);
    // The reset leaves no controller at all, so ask for a fresh preview instead
    // of falling back to a placeholder: column 2 must never be empty.
    requestPreview();
    setRunning(false);
    setStatus('Reset. Press play to train a fresh network.', 'info');
  }

  byId<HTMLButtonElement>('play-button').addEventListener('click', () => {
    void startTraining();
  });

  byId<HTMLButtonElement>('pause-button').addEventListener('click', pauseTraining);

  byId<HTMLButtonElement>('reset-button').addEventListener('click', () => {
    void resetTraining();
  });

  byId<HTMLButtonElement>('add-neuron-button').addEventListener('click', () => {
    const target = config.hidden[0];
    if (!initialized || !target) {
      setStatus('Press play to build a network first.', 'error');
      return;
    }
    if (!canAddNeuron(target)) {
      setStatus(`Layer ${target.id} already has ${LIMITS.maxUnitsPerLayer} neurons.`, 'error');
      return;
    }
    client.send({
      type: 'grow',
      request: { kind: 'neuron', layerId: target.id },
      strategyId: 'naive',
    });
  });

  byId<HTMLButtonElement>('add-layer-button').addEventListener('click', () => {
    if (!initialized) {
      setStatus('Press play to build a network first.', 'error');
      return;
    }
    if (!canAddLayer(config)) {
      setStatus(`At most ${LIMITS.maxHiddenLayers} hidden layers are supported.`, 'error');
      return;
    }
    client.send({
      type: 'grow',
      request: { kind: 'layer', units: LIMITS.defaultUnits },
      strategyId: 'naive',
    });
  });

  byId<HTMLButtonElement>('predict-button').addEventListener('click', () => {
    if (training) {
      setStatus('Pause training to run a prediction.', 'error');
      return;
    }
    if (!initialized) {
      setStatus('Press play to train a network first.', 'error');
      return;
    }
    const input = pad.toInput();
    if (!input) {
      setStatus('Draw a digit first.', 'error');
      return;
    }
    client.send({ type: 'predict', pixels: input }, [input.buffer]);
  });

  byId<HTMLButtonElement>('clear-button').addEventListener('click', () => pad.clear());

  byId<HTMLInputElement>('hidden-units').addEventListener('input', () => {
    if (training) {
      return;
    }
    config = readConfig();
    byId<HTMLSpanElement>('hidden-units-value').textContent = String(config.hidden[0]?.units ?? 0);
    renderParameterSummary(config);
    schedulePreview();
  });

  setStatus('Ready. Press play to train.', 'info');
  // Build the initial topology immediately so the graph shows a real network
  // from the first frame instead of a placeholder.
  requestPreview();

  window.addEventListener('beforeunload', () => client.terminate());
}

void main();
